import { motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import gsap from 'gsap';

export default function HeroSection() {
  const titleRef = useRef<HTMLHeadingElement>(null);
  const subtitleRef = useRef<HTMLParagraphElement>(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth - 0.5) * 20,
        y: (e.clientY / window.innerHeight - 0.5) * 20,
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  useEffect(() => {
    if (titleRef.current && subtitleRef.current) {
      gsap.fromTo(
        titleRef.current,
        { opacity: 0, y: 50, scale: 0.8 },
        { opacity: 1, y: 0, scale: 1, duration: 1.2, ease: 'power3.out' }
      );
      gsap.fromTo(
        subtitleRef.current,
        { opacity: 0, y: 30 },
        { opacity: 1, y: 0, duration: 1, delay: 0.3, ease: 'power3.out' }
      );
    }
  }, []);

  return (
    <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Animated gradient background */}
      <div className="absolute inset-0 bg-gradient-to-b from-black via-purple-900/20 to-black">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(0,255,255,0.1),transparent_50%)] animate-pulse"></div>
      </div>

      {/* Floating geometric shapes */}
      <motion.div
        animate={{
          x: [0, 100, 0],
          y: [0, -100, 0],
          rotate: [0, 180, 360],
        }}
        transition={{
          duration: 20,
          repeat: Infinity,
          ease: "linear"
        }}
        className="absolute top-20 left-10 w-32 h-32 border-2 border-cyan-500/30 rounded-3xl"
        style={{ transform: `translate(${mousePosition.x}px, ${mousePosition.y}px)` }}
      />
      
      <motion.div
        animate={{
          x: [0, -80, 0],
          y: [0, 80, 0],
          rotate: [0, -180, -360],
        }}
        transition={{
          duration: 15,
          repeat: Infinity,
          ease: "linear"
        }}
        className="absolute bottom-20 right-10 w-40 h-40 border-2 border-purple-500/30 rounded-full"
        style={{ transform: `translate(${-mousePosition.x}px, ${-mousePosition.y}px)` }}
      />

      <motion.div
        animate={{
          scale: [1, 1.2, 1],
          rotate: [0, 90, 0],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "easeInOut"
        }}
        className="absolute top-1/2 right-20 w-24 h-24 border-2 border-pink-500/30"
        style={{ transform: `translate(${mousePosition.x * 0.5}px, ${mousePosition.y * 0.5}px)` }}
      />
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center">
        <motion.div
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ duration: 0.8, type: "spring" }}
          className="mb-8"
        >
          <motion.div
            animate={{
              boxShadow: [
                "0 0 20px rgba(0,255,255,0.3)",
                "0 0 40px rgba(168,85,247,0.4)",
                "0 0 20px rgba(0,255,255,0.3)",
              ]
            }}
            transition={{ duration: 2, repeat: Infinity }}
            className="inline-block px-6 py-2 rounded-full bg-gradient-to-r from-cyan-500/20 to-purple-500/20 border border-cyan-500/30 backdrop-blur-sm"
          >
            <span className="text-cyan-400 text-sm font-medium">🚀 Ecosistema 3D Integral</span>
          </motion.div>
        </motion.div>

        <motion.h1
          ref={titleRef}
          animate={{
            textShadow: [
              "0 0 20px rgba(0,255,255,0.5)",
              "0 0 40px rgba(168,85,247,0.5)",
              "0 0 20px rgba(0,255,255,0.5)",
            ]
          }}
          transition={{ duration: 3, repeat: Infinity }}
          className="text-5xl md:text-7xl lg:text-8xl font-bold mb-6 leading-tight"
        >
          <motion.span
            animate={{ backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"] }}
            transition={{ duration: 5, repeat: Infinity }}
            className="bg-gradient-to-r from-cyan-400 via-purple-500 to-pink-500 bg-clip-text text-transparent"
            style={{ backgroundSize: "200% 200%" }}
          >
            NEXUS FLOW
          </motion.span>
          <br />
          <motion.span
            animate={{ opacity: [0.8, 1, 0.8] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="text-white"
          >
            DYNAMICS
          </motion.span>
        </motion.h1>

        <motion.p
          ref={subtitleRef}
          animate={{ opacity: [0.7, 1, 0.7] }}
          transition={{ duration: 3, repeat: Infinity }}
          className="text-xl md:text-2xl text-gray-300 mb-12 max-w-3xl mx-auto leading-relaxed"
        >
          Plataforma inmersiva 3D que conecta IA, desarrollo web y educación en una experiencia de realidad digital avanzada
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-6"
        >
          <motion.a
            href="#services"
            whileHover={{ scale: 1.1, rotate: 2 }}
            whileTap={{ scale: 0.95 }}
            animate={{
              boxShadow: [
                "0 0 20px rgba(0,255,255,0.5)",
                "0 0 40px rgba(0,255,255,0.8)",
                "0 0 20px rgba(0,255,255,0.5)",
              ]
            }}
            transition={{ boxShadow: { duration: 2, repeat: Infinity } }}
            className="px-8 py-4 rounded-full bg-gradient-to-r from-cyan-500 to-purple-600 text-white font-semibold text-lg hover:shadow-2xl hover:shadow-cyan-500/50 transition-all duration-300 transform whitespace-nowrap cursor-pointer"
          >
            Explorar Ecosistema
          </motion.a>
          <motion.a
            href="#packages"
            whileHover={{ scale: 1.1, rotate: -2 }}
            whileTap={{ scale: 0.95 }}
            className="px-8 py-4 rounded-full border-2 border-cyan-500 text-cyan-400 font-semibold text-lg hover:bg-cyan-500/10 transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            Ver Paquetes
          </motion.a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-8"
        >
          {[
            { icon: 'ri-robot-2-line', label: 'Agentes IA', color: 'cyan', delay: 0 },
            { icon: 'ri-code-s-slash-line', label: 'Desarrollo Web', color: 'purple', delay: 0.1 },
            { icon: 'ri-graduation-cap-line', label: 'Educación', color: 'pink', delay: 0.2 },
            { icon: 'ri-line-chart-line', label: 'Trading', color: 'yellow', delay: 0.3 }
          ].map((item) => (
            <motion.div
              key={item.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1 + item.delay }}
              whileHover={{ scale: 1.15, y: -10 }}
              className="group cursor-pointer"
            >
              <motion.div
                animate={{
                  rotate: [0, 5, -5, 0],
                  scale: [1, 1.05, 1],
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  delay: item.delay,
                }}
                className={`w-16 h-16 mx-auto mb-3 rounded-2xl bg-gradient-to-br from-${item.color}-400/20 to-${item.color}-600/20 border border-${item.color}-500/30 flex items-center justify-center transform group-hover:shadow-lg group-hover:shadow-${item.color}-500/50 transition-all duration-300`}
              >
                <motion.i
                  animate={{ rotate: [0, 360] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
                  className={`${item.icon} text-3xl text-${item.color}-400`}
                ></motion.i>
              </motion.div>
              <p className="text-gray-400 text-sm font-medium">{item.label}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>

      <motion.div
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 1.5, repeat: Infinity }}
        className="absolute bottom-10 left-1/2 transform -translate-x-1/2"
      >
        <a href="#services" className="w-10 h-10 flex items-center justify-center cursor-pointer">
          <motion.i
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="ri-arrow-down-line text-3xl text-cyan-400"
          ></motion.i>
        </a>
      </motion.div>
    </section>
  );
}
